import { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Thème global de Perrinos.
 *
 * Le choix vit sur `<html data-theme="dark|light">` afin que toutes les
 * pages (landing, Papyrus, authentification, espace connecté) partagent
 * les mêmes jetons. Sombre par défaut, conformément au handoff de la
 * landing ; crème disponible en bascule et persisté dans localStorage.
 */
const STORAGE_KEY = "perrinos-theme";
export const DEFAULT_THEME = "dark";

const THEME_COLORS = { dark: "#0B1A2E", light: "#FDF3ED" };

function readStoredTheme() {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function applyTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLORS[theme]);
}

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  toggleTheme: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* stockage indisponible (navigation privée) — le thème reste en mémoire */
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next === "light" || next === "dark") setThemeState(next);
  }, []);

  const toggleTheme = useCallback(
    () => setThemeState((current) => (current === "dark" ? "light" : "dark")),
    []
  );

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
